import { useEffect, useState } from "react";
import SectionHeader from "../eventsExperiences/SectionHeader";
// A format's three images: aiFormats/<name>1..3-1600.jpg, the 1600px web
// copies of the originals (full-size ones are far too heavy to animate).
const shots = (name) =>
  [1, 2, 3].map((i) => require(`../../assets/images/whatwedo/aiContentCreation/aiFormats/${name}${i}-1600.jpg`));

// `ratio` is the screen's shape for that format; `images` cycle inside it.
const FORMATS = [
  {
    ratio: 16 / 9, label: "16:9", title: "Long-form videos", images: shots("long"),
    desc: "Brand films, explainers, documentaries, podcasts and YouTube videos. Scripted, visualised and edited to hold attention for minutes, not seconds.",
    bestFor: "Brand launches, product explainers, founder stories",
  },
  {
    ratio: 9 / 16, label: "9:16", title: "Short-form videos", images: shots("short"),
    desc: "Reels, Shorts and ad videos of 15 to 60 seconds. Each idea comes with several hooks and cuts, so you can see which one performs best.",
    bestFor: "Product promos, offers, performance ads",
  },
  {
    ratio: 2.39, label: "Cinema", title: "Film production", images: shots("film"),
    desc: "Ad films, short films and music videos. AI creates scenes, locations and effects that would be slow or costly to shoot. Our directors own the story, pacing, sound and final grade.",
    bestFor: "Campaign films, music videos, OTT and digital ads",
  },
  {
    ratio: 4 / 3, label: "Series", title: "Brand stories", images: shots("brand"),
    desc: "Your origin, your founder, your product and your people, told as a short film or a series that builds trust with customers, partners and investors.",
    bestFor: "About-us films, anniversaries, investor and press kits",
  },
  {
    ratio: 1, label: "1:1 · 4:5", title: "Product and ad creatives", images: shots("product"),
    desc: "Product visuals, posters, social creatives and promo videos, created without a studio shoot and sized for every platform.",
    bestFor: "D2C and e-commerce brands, retail offers, catalogues",
  },
  {
    ratio: 16 / 9, label: "HI · EN · +", title: "Multi-language versions", images: shots("multi"),
    desc: "One video adapted into Hindi, English and regional languages, with matching voice, captions and on-screen text.",
    bestFor: "Pan-India campaigns, regional launches, training videos",
  },
];

const n = FORMATS.length;
// Signed distance from the active format, wrapping around the ends.
const offsetOf = (i, active) => ((i - active + n + n / 2) % n) - n / 2;
// The screen fits inside 560 × 360 in its format's own shape.
const screenSize = (r) => (r >= 560 / 360 ? [560, 560 / r] : [360 * r, 360]);

// One format at a time on the centre screen (its images cross-fade); the
// others wait as side cards and step in one by one every 3.6s.
const AiFormats = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setActive((a) => (a + 1) % n), 3600);
    return () => clearInterval(timer);
  }, [active]); // a click restarts the countdown

  const { ratio, label, title, desc, bestFor, images } = FORMATS[active];
  const [w, h] = screenSize(ratio);

  return (
    <section className="ee-section ai-formats">
      <div className="container">
        <SectionHeader
          eyebrow="Our formats"
          title="What we"
          accent="create."
          intro="Six types of content, one team. Pick one, or let us recommend the mix that fits your goal and budget."
        />

        <div className="ai-showcase">
          <svg className="ai-ribbon" viewBox="0 0 1200 420" preserveAspectRatio="none" aria-hidden="true">
            <path d="M-40 360 C 220 380, 300 60, 520 90 S 760 420, 960 300 S 1180 40, 1260 80" />
          </svg>

          {FORMATS.map((f, i) => {
            const o = offsetOf(i, active);
            // Side cards hug the screen's current width, then step out 210 apart.
            const x = o && Math.sign(o) * (w / 2 + 130) + (o - Math.sign(o)) * 210;
            return (
              <button
                type="button"
                key={f.title}
                className={`ai-thumb${o === 0 || Math.abs(o) > 2 ? " is-hidden" : ""}`}
                style={{ "--x": x, "--o": o }}
                onClick={() => setActive(i)}
                aria-label={f.title}
              >
                <img src={f.images[i % f.images.length]} alt="" loading="lazy" />
                <span>{f.title}</span>
              </button>
            );
          })}

          <div className="ai-screen" style={{ "--w": w, "--h": h }}>
            {/* <span className="ai-screen-label">{label}</span> */}
            {/* Re-keyed per format so its images restart from the first. */}
            <div className="ai-screen-view" key={active}>
              {images.map((src) => (
                <img key={src} src={src} alt="" />
              ))}
            </div>
          </div>
        </div>

        <div className="ai-format-info" key={active}>
          <h3>{title}</h3>
          <p>{desc}</p>
          <small><span>Best for</span> {bestFor}</small>
        </div>
      </div>
    </section>
  );
};

export default AiFormats;
